import { useMemo } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export interface BuyAgainResult {
  successCount: number;
  failedCount: number;
  failedItems: { productDescriptor: string; productName: string; reason: string }[];
}

type Props = {
  open: boolean;
  onClose: () => void;
  result: BuyAgainResult | null;
};

export default function BuyAgainResultDialog({ open, onClose, result }: Props) {
  const groupedFailures = useMemo(() => {
    if (!result) return {};
    const groups: Record<string, string[]> = {};
    for (const item of result.failedItems) {
      if (!groups[item.reason]) {
        groups[item.reason] = [];
      }
      groups[item.reason].push(item.productName || item.productDescriptor);
    }
    return groups;
  }, [result]);

  if (!result) return null;

  const hasFailures = result.failedCount > 0;
  const hasSuccesses = result.successCount > 0;
  // The cart can report a shortfall it cannot attribute to specific lines, so failedCount may exceed
  // the named failures.
  const hasNamedFailures = result.failedItems.length > 0;
  const unnamedFailureCount = Math.max(0, result.failedCount - result.failedItems.length);
  const reasonsHint = hasNamedFailures
    ? " Review the reasons below."
    : " They may no longer be available in your product catalog.";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 0 }}>
        {hasSuccesses && !hasFailures && (
          <Alert severity="success" action={<IconButton size="small" aria-label="Close" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>}>
            <AlertTitle sx={{ fontWeight: 700 }}>Items added to cart</AlertTitle>
            {result.successCount} item(s) successfully added to cart.
          </Alert>
        )}
        {hasFailures && (
          <Alert
            severity="error"
            action={<IconButton size="small" aria-label="Close" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>}
            sx={{ "& .MuiAlert-message": { width: "100%" } }}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>Failed to add items to cart</AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {hasSuccesses
                ? `${result.successCount} item(s) added successfully, but ${result.failedCount} could not be added.${reasonsHint}`
                : hasNamedFailures
                  ? "One or more items could not be added to the cart. Review the reasons below."
                  : `${result.failedCount} item(s) could not be added to the cart.${reasonsHint}`}
            </Typography>
            {hasNamedFailures && (
              <Box component="ul" sx={{ pl: 2, mb: 0, mt: 1 }}>
                {Object.entries(groupedFailures).map(([reason, products]) => (
                  <li key={reason}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {reason}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 1 }}>
                      {products.map((name, idx) => (
                        <li key={idx}>
                          <Typography variant="body2">{name}</Typography>
                        </li>
                      ))}
                    </Box>
                  </li>
                ))}
                {unnamedFailureCount > 0 && (
                  <li>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {`${unnamedFailureCount} other item(s) could not be added.`}
                    </Typography>
                  </li>
                )}
              </Box>
            )}
          </Alert>
        )}
      </DialogContent>
      {hasSuccesses && hasFailures && (
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
